/**
 * Where every pull centre stands ACROSS one frame, rather than at one instant
 * of it. `src/sim/trajectory.ts` is the same grid over a horizon of days and is
 * this module's twin; keep the two in step.
 */

import { stepFlight } from './flight.ts';
import type {
  FlightAxes, FlightConfig, FlightField, FlightPull, FlightState, FlightVec,
} from './flight.ts';
import type { FlightGround } from './ground.ts';

/**
 * Substeps one span holds its centres for: `STEPS_PER_KNOT` in
 * `src/sim/trajectory.ts`, and the same trade. A span is 8 to 16 simulated
 * seconds whatever the frame length, so the error per unit sim time is a
 * property of the span rather than of how long the frame took.
 */
export const CENTRE_SPAN_SUBSTEPS = 8;

/** Knots a revolution one row's grid gets: `SAMPLES_PER_PERIOD` in its twin. */
export const CENTRE_KNOTS_PER_PERIOD = 32;

/**
 * The most intervals one row's grid holds. The longest frame any warp level can
 * ask for wants 11 of them, for Phobos; the cap is only there so a row with an
 * absurd period cannot size an allocation off it.
 */
const CENTRE_INTERVAL_CAP = 64;

/** One slot's centre and velocity at `jd`, in metres and metres a second. */
export type CentreSampler = (
  slot: number, jd: number, pos: FlightVec, vel: FlightVec,
) => void;

export interface FlightCentres {
  /**
   * Lay every pulling slot's knots across one frame. `false` when there is
   * nothing to interpolate - a frame of no simulated time, or an epoch that did
   * not move - and the caller should hold the centres the way it did before.
   */
  lay(
    count: number,
    gm: readonly FlightPull[],
    periodS: readonly number[],
    jd0: number,
    jd1: number,
    dtS: number,
    sample: CentreSampler,
  ): boolean;
  /** Every laid slot's centre `tauS` simulated seconds into the frame. */
  readInto(tauS: number, pulls: readonly FlightPull[]): void;
  /**
   * The same centres into a set of points of the caller's own, so a reader can
   * hold two epochs at once. A slot with no grid laid keeps `pulls`'s centre,
   * which is what the caller would have read anyway.
   */
  readPointsInto(
    tauS: number, into: readonly FlightVec[], pulls: readonly FlightPull[],
    parentIdx: number,
  ): void;
  /** Ephemeris samples the last `lay()` took, for a cost readout. */
  readonly samples: number;
}

export function createFlightCentres(): FlightCentres {
  const pos: Float64Array[] = [];
  const vel: Float64Array[] = [];
  const intervals: number[] = [];
  const stepS: number[] = [];
  let live = 0;
  let samples = 0;
  const _p: FlightVec = { x: 0, y: 0, z: 0 };
  const _v: FlightVec = { x: 0, y: 0, z: 0 };

  function ensure(i: number, n: number): void {
    const want = (n + 1) * 3;
    while (pos.length <= i) {
      pos.push(new Float64Array(0));
      vel.push(new Float64Array(0));
      intervals.push(0);
      stepS.push(0);
    }
    if (pos[i]!.length < want) {
      pos[i] = new Float64Array(want);
      vel[i] = new Float64Array(want);
    }
  }

  function lay(
    count: number,
    gm: readonly FlightPull[],
    periodS: readonly number[],
    jd0: number,
    jd1: number,
    dtS: number,
    sample: CentreSampler,
  ): boolean {
    live = 0;
    samples = 0;
    if (!(dtS > 0) || !(jd1 > jd0)) return false;
    // Days a simulated second is worth over THIS frame, so the last knot lands
    // on the epoch the registry was just placed at rather than a rounding of it.
    const jdPerS = (jd1 - jd0) / dtS;
    for (let i = 0; i < count; i++) {
      ensure(i, 1);
      intervals[i] = 0;
      const pull = gm[i];
      if (pull === undefined || !(pull.gm > 0)) continue;
      const period = periodS[i] ?? 0;
      const want = period > 0
        ? Math.ceil((dtS * CENTRE_KNOTS_PER_PERIOD) / period) : 1;
      const n = want < 1 ? 1 : (want > CENTRE_INTERVAL_CAP ? CENTRE_INTERVAL_CAP : want);
      ensure(i, n);
      const h = dtS / n;
      intervals[i] = n;
      stepS[i] = h;
      const p = pos[i]!;
      const v = vel[i]!;
      for (let k = 0; k <= n; k++) {
        sample(i, jd0 + k * h * jdPerS, _p, _v);
        const o = k * 3;
        p[o] = _p.x;
        p[o + 1] = _p.y;
        p[o + 2] = _p.z;
        v[o] = _v.x;
        v[o + 1] = _v.y;
        v[o + 2] = _v.z;
        samples++;
      }
    }
    live = count;
    return true;
  }

  /** One laid slot's centre at `tauS`, into `out`. The caller checks it is laid. */
  function evaluate(i: number, tauS: number, out: FlightVec): void {
    const n = intervals[i]!;
    const h = stepS[i]!;
    let k = h > 0 ? Math.floor(tauS / h) : 0;
    if (!(k >= 0)) k = 0;
    if (k > n - 1) k = n - 1;
    const t = h > 0 ? tauS / h - k : 0;
    const t2 = t * t;
    const t3 = t2 * t;
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;
    const a = k * 3;
    const b = a + 3;
    const p = pos[i]!;
    const v = vel[i]!;
    out.x = h00 * p[a]! + h10 * h * v[a]! + h01 * p[b]! + h11 * h * v[b]!;
    out.y = h00 * p[a + 1]! + h10 * h * v[a + 1]! + h01 * p[b + 1]! + h11 * h * v[b + 1]!;
    out.z = h00 * p[a + 2]! + h10 * h * v[a + 2]! + h01 * p[b + 2]! + h11 * h * v[b + 2]!;
  }

  function readInto(tauS: number, pulls: readonly FlightPull[]): void {
    for (let i = 0; i < live; i++) {
      if ((intervals[i] ?? 0) <= 0) continue;
      const slot = pulls[i];
      if (slot === undefined) continue;
      evaluate(i, tauS, slot.at);
    }
  }

  function readPointsInto(
    tauS: number, into: readonly FlightVec[], pulls: readonly FlightPull[],
    parentIdx: number,
  ): void {
    const n = into.length < pulls.length ? into.length : pulls.length;
    for (let i = 0; i < n; i++) {
      const out = into[i]!;
      if (i < live && (intervals[i] ?? 0) > 0) {
        evaluate(i, tauS, out);
        continue;
      }
      const at = pulls[i]!.at;
      out.x = at.x;
      out.y = at.y;
      out.z = at.z;
    }
    // Onto the held parent, which is where the caller's own evaluation point
    // stands: without this the set is a whole epoch of the parent's travel away
    // from the ship it is about to be differenced against.
    const held = parentIdx >= 0 && parentIdx < n ? pulls[parentIdx]!.at : null;
    if (held === null) return;
    const p = into[parentIdx]!;
    const dx = held.x - p.x;
    const dy = held.y - p.y;
    const dz = held.z - p.z;
    for (let i = 0; i < n; i++) {
      const out = into[i]!;
      out.x += dx;
      out.y += dy;
      out.z += dz;
    }
  }

  return {
    lay,
    readInto,
    readPointsInto,
    get samples() { return samples; },
  };
}

/**
 * Everything one frame of flight is flown against. `src/main.ts` holds these as
 * module state and fills this in place once a frame; `tools/preview/
 * transit-check.mjs` fills the same shape off its own registry, which is what
 * lets a headless run fly the entry the game flies rather than a copy of it.
 */
export interface FlightFrameContext {
  state: FlightState;
  config: FlightConfig;
  /** The evaluation point, moved onto each span's own epoch as the frame runs. */
  field: FlightField;
  centres: FlightCentres;
  pulls: readonly FlightPull[];
  /** Slots the last fill actually filled. */
  pullCount: number;
  periodS: readonly number[];
  /** Slot the frame is flown in, or -1 for an inertial frame. */
  parentIdx: number;
  /** TDB Julian dates the frame runs between. */
  jd0: number;
  jd1: number;
  sample: CentreSampler;
  /** Put `field.frameAccel` at the parent's own interpolated centre. */
  frameAccelAt: (parentIdx: number) => void;
  /** No grid at all: one `stepFlight` over the whole frame, centres held. */
  centresHeld?: boolean;
  /**
   * Override for `CENTRE_SPAN_SUBSTEPS`, so a test can widen or shrink the
   * cadence without a second copy of the loop. Unset is the shipped span.
   */
  spanSubsteps?: number;
}

const _spanFlown: FlightVec = { x: 0, y: 0, z: 0 };
const _shipRel: FlightVec = { x: 0, y: 0, z: 0 };

// The ground a no-grid frame installs `readLinearGround` over, and the frame
// length that reader extrapolates against: `CentreReader` carries a field for
// neither, so the reader reaches them through these module-level slots instead
// of a closure allocated fresh every frame.
let _linearGround: FlightGround | null = null;
let _linearFrameDt = 0;

/**
 * A no-grid CentreReader: each slot's centre linearly extrapolated from where
 * `pulls` holds it using `ground.velIn`, the slot's own velocity in the flight
 * frame. `pulls` is refilled at the frame's own END before a step runs
 * (`refillFlightField` in `src/main.ts`), not its start, so the anchor this
 * extrapolates from sits `_linearFrameDt` seconds into the frame, and `tauS`
 * short of the full frame is `tauS - _linearFrameDt` seconds - negative until
 * the last substep. `velIn` already reads zero for the frame's own parent, so
 * the parent's centre comes back exactly the held one with no extra shift -
 * the same cancellation `readPointsInto` forces by hand for the laid case.
 */
function readLinearGround(
  tauS: number, into: readonly FlightVec[], pulls: readonly FlightPull[],
): void {
  const ground = _linearGround!;
  const velIn = ground.velIn!;
  const dt = tauS - _linearFrameDt;
  const n = Math.min(into.length, pulls.length, velIn.length);
  for (let i = 0; i < n; i++) {
    const at = pulls[i]!.at;
    const v = velIn[i]!;
    const out = into[i]!;
    out.x = at.x + v.x * dt;
    out.y = at.y + v.y * dt;
    out.z = at.z + v.z * dt;
  }
}

// One frame of flight, flown in spans of CENTRE_SPAN_SUBSTEPS substeps so every
// centre and the ship stand at one epoch; one call where no grid can be laid.
export function stepFlightSpans(
  ctx: FlightFrameContext,
  axes: FlightAxes,
  throttle: number,
  dt: number,
  attitudeDt: number,
  substeps: number,
  out: FlightVec,
  assistDt: number,
): void {
  const parent = ctx.parentIdx;
  const laid = ctx.centresHeld !== true && parent >= 0 && substeps > 1 && dt > 0
    && ctx.centres.lay(
      ctx.pullCount, ctx.pulls, ctx.periodS, ctx.jd0, ctx.jd1, dt, ctx.sample);
  // A surface the ship can meet needs its centre at both ends of the substep
  // being swept, not once a span: Phobos covers more than its own radius in
  // half a span, so a held centre both misses hits and invents them.
  const ground = ctx.field.ground ?? null;
  if (ground !== null) {
    if (laid) {
      ground.centreAt = ctx.centres.readPointsInto;
    } else if (ground.velIn !== null) {
      _linearGround = ground;
      _linearFrameDt = dt;
      ground.centreAt = readLinearGround;
    } else {
      ground.centreAt = null;
    }
    ground.tau0 = 0;
    ground.centreParent = parent;
  }
  if (!laid) {
    stepFlight(ctx.state, axes, throttle, ctx.config, dt, attitudeDt, out, ctx.field, assistDt);
    return;
  }
  // The slot the interpolant writes into, so reading it back after a span is
  // the parent's own place at that span's epoch.
  const centre = ctx.pulls[parent]!.at;
  _shipRel.x = ctx.field.at.x - centre.x;
  _shipRel.y = ctx.field.at.y - centre.y;
  _shipRel.z = ctx.field.at.z - centre.z;
  out.x = 0;
  out.y = 0;
  out.z = 0;
  const h = dt / substeps;
  const spanSubsteps = ctx.spanSubsteps ?? CENTRE_SPAN_SUBSTEPS;
  let done = 0;
  while (done < substeps) {
    const take = Math.min(spanSubsteps, substeps - done);
    const spanS = take * h;
    if (ground !== null) ground.tau0 = done * h;
    ctx.centres.readInto(Math.min(done * h + spanS * 0.5, dt), ctx.pulls);
    ctx.frameAccelAt(parent);
    ctx.field.at.x = centre.x + _shipRel.x;
    ctx.field.at.y = centre.y + _shipRel.y;
    ctx.field.at.z = centre.z + _shipRel.z;
    // The hand's own rotation is one turn for the frame, not one per span.
    stepFlight(ctx.state, axes, throttle, ctx.config, spanS, done === 0 ? attitudeDt : 0,
      _spanFlown, ctx.field, assistDt > 0 ? (assistDt * spanS) / dt : 0);
    _shipRel.x += _spanFlown.x;
    _shipRel.y += _spanFlown.y;
    _shipRel.z += _spanFlown.z;
    out.x += _spanFlown.x;
    out.y += _spanFlown.y;
    out.z += _spanFlown.z;
    done += take;
  }
}
